/**
 * Session validity helpers shared by the router, the session guard and every
 * "open in a new tab" action.
 *
 * The API token is a JWT whose `exp` claim is the real logout time. Reading it
 * locally lets the app decide *before* rendering or opening a protected page
 * instead of waiting for the first 401 to bounce the user out.
 *
 * Tokens that are not JWTs, or carry no `exp` (test tokens, super-admin flows),
 * are treated as valid so every existing login path keeps working unchanged.
 */

/** Fired on this tab whenever the session is created, ended or invalidated. */
export const SESSION_CHANGED_EVENT = "session-changed";

/** localStorage key used only to notify the other tabs of this browser. */
const SESSION_SIGNAL_KEY = "session_signal";

/** Expire slightly early so an in-flight request cannot race the real expiry. */
const EXPIRY_SKEW_MS = 5000;

/** Authentication data shared by every tab in this browser. */
const AUTH_STORAGE_KEYS = [
    "authToken",
    "authUser",
    "shipez.session_id",
    "shipez.pendingUser",
    "shipez.pendingToken",
    "shipez.pendingResult",
    "isSuperAdmin",
    "MasterCompanyDetails",
];

/**
 * Screens that work without a session. Nothing session-related may block or
 * redirect these — in particular the sign-in screens, where a "your session
 * expired, please refresh" dialog would cover the form the user needs.
 */
export const PUBLIC_PATH_PREFIXES = [
    "/login",
    "/signin",
    "/signup",
    "/quote-response",
    "/locker-freight/login",
    "/infox/login",
    "/limco/login",
];

export const isPublicPath = (pathname = "") =>
    PUBLIC_PATH_PREFIXES.some(
        (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
    );

const readStorage = (storage, key) => {
    try {
        return storage.getItem(key);
    } catch {
        return null;
    }
};

const base64UrlDecode = (segment) => {
    const padded = segment.replace(/-/g, "+").replace(/_/g, "/");
    return atob(padded + "=".repeat((4 - (padded.length % 4)) % 4));
};

/** JWT payload as an object, or null when the token is not a readable JWT. */
export const decodeJwtPayload = (token) => {
    const parts = String(token || "").split(".");
    if (parts.length !== 3) return null;
    try {
        return JSON.parse(base64UrlDecode(parts[1]));
    } catch {
        return null;
    }
};

// The expiry never changes for a given token, so decode each one only once —
// these helpers run on every click and on every poll.
let expiryCacheToken = null;
let expiryCacheValue = null;

/** Token expiry in epoch milliseconds, or null when the token carries none. */
export const getTokenExpiryMs = (token) => {
    if (token && token === expiryCacheToken) return expiryCacheValue;
    const payload = decodeJwtPayload(token);
    const exp = payload?.exp;
    const expiresAt = typeof exp === "number" && Number.isFinite(exp) ? exp * 1000 : null;
    expiryCacheToken = token || null;
    expiryCacheValue = expiresAt;
    return expiresAt;
};

/**
 * How long the token was issued for (`iat`/`nbf` → `exp`) in milliseconds, or
 * null when the token does not say. Used to size the pre-expiry warning to the
 * real token lifetime instead of assuming one.
 */
export const getTokenLifetimeMs = (token) => {
    const payload = decodeJwtPayload(token);
    const exp = payload?.exp;
    const issuedAt = payload?.iat ?? payload?.nbf;
    if (typeof exp !== "number" || typeof issuedAt !== "number") return null;
    const lifetime = (exp - issuedAt) * 1000;
    return lifetime > 0 ? lifetime : null;
};

/** Unknown expiry counts as "not expired" — never lock out a non-JWT token. */
export const isTokenExpired = (token, { skewMs = EXPIRY_SKEW_MS } = {}) => {
    if (!token) return true;
    const expiresAt = getTokenExpiryMs(token);
    if (expiresAt == null) return false;
    return Date.now() >= expiresAt - skewMs;
};

export const getAuthToken = () => {
    if (typeof window === "undefined") return null;
    return readStorage(window.localStorage, "authToken");
};

/** True while this tab holds a token that has not expired yet. */
export const hasActiveSession = () => {
    const token = getAuthToken();
    return Boolean(token) && !isTokenExpired(token);
};

/** Milliseconds until the token expires; null when the expiry is unknown. */
export const getSessionTimeLeftMs = () => {
    const expiresAt = getTokenExpiryMs(getAuthToken());
    return expiresAt == null ? null : expiresAt - Date.now() - EXPIRY_SKEW_MS;
};

/**
 * Clear the browser's shared authentication.
 *
 * `broadcast` tells the other tabs to sign out too, so it must be reserved for
 * a deliberate sign-out. A tab that merely *observes* a dead session has to
 * clear itself only: authentication storage is shared, so a background tab
 * broadcasting its own stale verdict would wipe a session another tab has
 * since created — which bounces every tab back to the login screen in a loop.
 *
 * `expectedToken` is the token the caller believed was active. When storage
 * already holds a different one a newer login has landed, the caller's verdict
 * is stale and nothing is cleared. Returns whether the session was ended.
 */
export const endSession = ({
    reason = "expired",
    broadcast = true,
    notifyLocal = true,
    expectedToken,
} = {}) => {
    if (typeof window === "undefined") return false;

    if (expectedToken !== undefined && getAuthToken() !== expectedToken) {
        return false;
    }

    for (const key of AUTH_STORAGE_KEYS) {
        try {
            window.localStorage.removeItem(key);
            window.sessionStorage.removeItem(key);
        } catch {
            /* storage blocked — continue clearing what is accessible */
        }
    }

    if (broadcast) {
        try {
            window.localStorage.setItem(
                SESSION_SIGNAL_KEY,
                JSON.stringify({ reason, at: Date.now() }),
            );
        } catch {
            /* storage blocked — the in-tab event below still fires */
        }
    }

    if (notifyLocal) {
        try {
            window.dispatchEvent(
                new CustomEvent(SESSION_CHANGED_EVENT, { detail: { reason } }),
            );
        } catch {
            /* older browsers without CustomEvent constructor */
        }
    }

    return true;
};

/**
 * Run `handler` whenever the session may have changed: in this tab, in another
 * tab of the same browser, or when the tab regains focus after being idle.
 *
 * The handler receives the reason that ended the session ("logout", "expired",
 * "unauthorized", ...) so callers can word their message accordingly.
 */
export const subscribeToSessionChanges = (handler) => {
    if (typeof window === "undefined") return () => {};

    const onLocal = (event) => handler(event?.detail?.reason ?? "changed");

    const onStorage = (event) => {
        if (!event) return;
        if (event.key === SESSION_SIGNAL_KEY) {
            let reason = "changed";
            try {
                reason = JSON.parse(event.newValue || "{}")?.reason ?? "changed";
            } catch {
                /* malformed signal — still treat it as a session change */
            }
            handler(reason);
            return;
        }
        if (event.key === "authToken" || event.key === null) handler("changed");
    };

    const onVisible = () => {
        if (document.visibilityState === "visible") handler("changed");
    };

    window.addEventListener(SESSION_CHANGED_EVENT, onLocal);
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", onLocal);
    document.addEventListener("visibilitychange", onVisible);

    return () => {
        window.removeEventListener(SESSION_CHANGED_EVENT, onLocal);
        window.removeEventListener("storage", onStorage);
        window.removeEventListener("focus", onLocal);
        document.removeEventListener("visibilitychange", onVisible);
    };
};

/**
 * Gate a protected navigation (in-page or new tab).
 *
 * Returns true when the caller may continue. When the session is already gone
 * it clears the tab and sends *this* window to the login screen, so the
 * protected page is never opened or rendered first.
 */
export const ensureActiveSession = ({ redirect = true, loginPath = "/login" } = {}) => {
    if (hasActiveSession()) return true;

    // Local only — this tab observed the expiry, it is not signing anyone else out.
    endSession({ reason: "expired", broadcast: false });

    if (redirect && typeof window !== "undefined") {
        if (window.location.pathname !== loginPath) {
            window.location.replace(loginPath);
        }
    }
    return false;
};
